import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Camera, Home as HomeIcon, Receipt, Loader2, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useToast } from '../components/ui/Toast';
import { expenseCaptureApi } from '../services/expenseCaptureApi';
import type { ExpenseCategory, ExpenseCompany, ExpenseReceipt, ExpenseStatus } from '../types/expense';
import { ExpenseHome } from '../components/expense/ExpenseHome';
import { MyExpensesList } from '../components/expense/MyExpensesList';
import { ExpenseCaptureWizard } from '../components/expense/ExpenseCaptureWizard';
import { ExpenseDetail } from '../components/expense/ExpenseDetail';
import { InstallPrompt } from '../components/expense/InstallPrompt';

// Capture-only mobile app. The country-manager review lives on its own desktop page
// (/expense-management), so there is no manager tab here.
type Tab = 'home' | 'list';
type Overlay =
    | { kind: 'wizard'; resumeReceiptId?: number; initialFile?: File }
    | { kind: 'detail'; receiptId: number };

export function ExpenseCapturePage() {
    const { user } = useAuth();
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/expense-capture');
    const { toast } = useToast();

    const [categories, setCategories] = useState<ExpenseCategory[]>([]);
    const [companies, setCompanies] = useState<ExpenseCompany[]>([]);
    const [defaultCompanyCode, setDefaultCompanyCode] = useState<string | null>(null);
    const [receipts, setReceipts] = useState<ExpenseReceipt[]>([]);
    const [loadingLookups, setLoadingLookups] = useState(true);
    const [loadingReceipts, setLoadingReceipts] = useState(true);

    const [tab, setTab] = useState<Tab>('home');
    const [listStatus, setListStatus] = useState<'all' | ExpenseStatus>('all');
    const [overlay, setOverlay] = useState<Overlay | null>(null);

    // Hidden inputs let the Home buttons + FAB open the camera / gallery in one tap,
    // then jump straight into the wizard with the captured file (no extra capture step).
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const uploadInputRef = useRef<HTMLInputElement>(null);

    const startCapture = (file: File | undefined) => {
        if (!file) return;
        if (!canEdit) { toast('You have view-only access.', 'info'); return; }
        setOverlay({ kind: 'wizard', initialFile: file });
    };

    const openCamera = () => cameraInputRef.current?.click();
    const openUpload = () => uploadInputRef.current?.click();

    const refreshReceipts = useCallback(() => {
        setLoadingReceipts(true);
        return expenseCaptureApi.listReceipts()
            .then(res => setReceipts(res.receipts))
            .catch(() => { /* keep prior list */ })
            .finally(() => setLoadingReceipts(false));
    }, []);

    // Load lookups + the user's own receipts once we have view access.
    useEffect(() => {
        if (!canView) return;
        let active = true;
        Promise.all([expenseCaptureApi.listCategories(), expenseCaptureApi.listCompanies()])
            .then(([cats, comps]) => {
                if (!active) return;
                setCategories(cats.categories);
                setCompanies(comps.companies);
                setDefaultCompanyCode(comps.defaultCompanyCode);
            })
            .catch(() => { if (active) toast('Could not load expense settings.', 'error'); })
            .finally(() => { if (active) setLoadingLookups(false); });

        void refreshReceipts();
        return () => { active = false; };
    }, [canView, refreshReceipts, toast]);

    if (permLoading) {
        return <div className="p-6 text-gray-400">Loading …</div>;
    }
    if (!canView) {
        return <div className="p-6 text-red-500">You do not have access to Expense Capture.</div>;
    }

    const openReceipt = (r: ExpenseReceipt) => {
        // Own drafts resume straight into the editor; submitted ones open read-only detail.
        if (r.Status === 'Draft' && r.UserEmail.toLowerCase() === (user?.email ?? '').toLowerCase()) {
            setOverlay({ kind: 'wizard', resumeReceiptId: r.Id });
        } else {
            setOverlay({ kind: 'detail', receiptId: r.Id });
        }
    };

    const closeOverlay = () => setOverlay(null);

    // ---- Overlays (full-screen takeovers) -----------------------------------
    if (overlay?.kind === 'wizard') {
        return (
            <Shell>
                <div className="mb-3 flex items-center gap-2">
                    <button onClick={closeOverlay} className="-ml-2 rounded-lg p-2 text-gray-500 hover:bg-gray-100">
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <h1 className="text-lg font-bold text-gray-900">
                        {overlay.resumeReceiptId ? 'Complete expense' : 'New expense'}
                    </h1>
                </div>
                {loadingLookups ? (
                    <CenterSpinner />
                ) : (
                    <ExpenseCaptureWizard
                        companies={companies}
                        categories={categories}
                        defaultCompanyCode={defaultCompanyCode}
                        resumeReceiptId={overlay.resumeReceiptId}
                        initialFile={overlay.initialFile}
                        onDone={() => { closeOverlay(); setTab('home'); void refreshReceipts(); }}
                        onCancel={() => { closeOverlay(); void refreshReceipts(); }}
                    />
                )}
            </Shell>
        );
    }

    if (overlay?.kind === 'detail') {
        return (
            <Shell>
                <ExpenseDetail
                    receiptId={overlay.receiptId}
                    currentUserEmail={user?.email ?? ''}
                    categories={categories}
                    onBack={closeOverlay}
                    onResume={(id) => setOverlay({ kind: 'wizard', resumeReceiptId: id })}
                    onChanged={() => void refreshReceipts()}
                />
            </Shell>
        );
    }

    // ---- Main tabbed view ----------------------------------------------------
    return (
        <Shell>
            <div className="mb-4 flex items-center gap-2">
                <Receipt className="h-6 w-6 text-blue-600" />
                <h1 className="text-xl font-bold text-gray-900">Expenses</h1>
            </div>

            <InstallPrompt />

            {/* Tabs */}
            <div className="my-3 flex gap-1 rounded-xl bg-gray-100 p-1">
                <TabButton active={tab === 'home'} onClick={() => setTab('home')} icon={HomeIcon} label="Home" />
                <TabButton active={tab === 'list'} onClick={() => setTab('list')} icon={Receipt} label="My expenses" />
            </div>

            <div className="pb-24">
                {tab === 'home' && (
                    <ExpenseHome
                        receipts={receipts}
                        onScanCamera={openCamera}
                        onUploadImage={openUpload}
                        onSelect={(r) => openReceipt(r)}
                        onViewAll={(s) => { setListStatus(s); setTab('list'); }}
                    />
                )}
                {tab === 'list' && (
                    <MyExpensesList
                        key={listStatus}
                        receipts={receipts}
                        loading={loadingReceipts}
                        onSelect={(r) => openReceipt(r)}
                    />
                )}
            </div>

            {/* Hidden inputs: camera opens the device camera; upload opens the gallery/files. */}
            <input
                ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; startCapture(f); }}
            />
            <input
                ref={uploadInputRef} type="file" accept="image/*,application/pdf" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; startCapture(f); }}
            />

            {/* Scan FAB — opens the camera directly */}
            {canEdit && (
                <button
                    type="button"
                    onClick={openCamera}
                    aria-label="Scan a receipt"
                    className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 active:scale-95"
                    style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
                >
                    <Camera className="h-6 w-6" />
                </button>
            )}
        </Shell>
    );
}

function Shell({ children }: { children: ReactNode }) {
    return <div className="mx-auto min-h-full max-w-xl px-1 sm:px-0">{children}</div>;
}

function CenterSpinner() {
    return (
        <div className="flex items-center justify-center gap-2 py-16 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
        </div>
    );
}

function TabButton({ active, onClick, icon: Icon, label }: {
    active: boolean; onClick: () => void; icon: typeof HomeIcon; label: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-medium transition ${
                active ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
        >
            <Icon className="h-4 w-4" /> {label}
        </button>
    );
}
